import { useCallback, useContext, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { SourceSyncOperationStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import type { UploadSyncResource } from "@gx/ts-contracts/services/uploader/resources/uploader_pb";

import {
  CREATE_SOURCE_PERMISSION_DENIED_HEADER,
  CREATE_SOURCE_PERMISSION_DENIED_SUBHEADER,
  CREATE_SOURCE_UNEXPECTED_ERROR,
} from "@/pages/sources/components/create/sources/constants";
import { CreateSourceFileUploadActionType } from "@/pages/sources/components/create/sources/file-upload/actions";
import {
  CREATE_SOURCE_FILE_UPLOAD_OPERATION_STEP_TO_ERROR_TOAST_HEADER_MAP,
  CREATE_SOURCE_FILE_UPLOAD_OPERATION_STEP_TO_PHASE_MAP,
} from "@/pages/sources/components/create/sources/file-upload/constants";
import {
  CreateSourceFileUploadContext,
  CreateSourceFileUploadContextShape,
} from "@/pages/sources/components/create/sources/file-upload/CreateSourceFileUploadProvider";
import {
  CreateSourceFileUploadOperation,
  CreateSourceFileUploadPhase,
} from "@/pages/sources/components/create/sources/file-upload/types";
import {
  buildCreateSourceFileUploadRequest,
  canSubmitUpload,
  extractProvisioningResult,
  getFileProgressMap,
  isUploadDisabled,
} from "@/pages/sources/components/create/sources/file-upload/utils";
import { useFileUploader } from "@/pages/sources/components/create/sources/hooks";
import type { FileUploadProgress } from "@/pages/sources/components/create/sources/types";
import {
  getCreateSourceNameError,
  isCreateSourceValueProvided,
} from "@/pages/sources/components/create/sources/utils";

import { ToastVariant } from "@/providers/toast/Toast";
import { useToast } from "@/providers/toast/useToast";

import { useCreateSourceMutation } from "@/api/mutations/sources";
import { createListSourcesInfiniteQueryKey } from "@/api/queries/sources";
import { isPermissionError } from "@/api/utils";

export function useCreateSourceFileUploadContext(): CreateSourceFileUploadContextShape {
  const context = useContext(CreateSourceFileUploadContext);
  if (!context) {
    throw new Error(
      "useCreateSourceFileUploadContext must be used within a CreateSourceFileUploadProvider",
    );
  }
  return context;
}

interface UseCreateSourceFileUploadOptions {
  onSuccess?: (sourceId: string) => void;
}

export function useCreateSourceFileUpload({ onSuccess }: UseCreateSourceFileUploadOptions) {
  const { state, dispatch } = useCreateSourceFileUploadContext();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { mutateAsync: createSourceMutation } = useCreateSourceMutation();

  const onSyncStart = useCallback(
    (sync: UploadSyncResource) => {
      dispatch({ type: CreateSourceFileUploadActionType.SET_SESSION, payload: sync });
    },
    [dispatch],
  );

  const onInitFileProgress = useCallback(
    (progressMap: Map<string, FileUploadProgress>) => {
      dispatch({ type: CreateSourceFileUploadActionType.INIT_FILE_PROGRESS, payload: progressMap });
      dispatch({
        type: CreateSourceFileUploadActionType.SET_PHASE,
        payload: CreateSourceFileUploadPhase.UPLOADING,
      });
    },
    [dispatch],
  );

  const onFileStart = useCallback(
    (operationId: string) => {
      dispatch({
        type: CreateSourceFileUploadActionType.UPDATE_FILE_PROGRESS,
        payload: { fileId: operationId, updates: { status: SourceSyncOperationStatus.RUNNING } },
      });
    },
    [dispatch],
  );

  const onPartComplete = useCallback(
    (operationId: string, partsComplete: number, percentage: number) => {
      dispatch({
        type: CreateSourceFileUploadActionType.UPDATE_FILE_PROGRESS,
        payload: { fileId: operationId, updates: { partsComplete, percentage } },
      });
    },
    [dispatch],
  );

  const onFileComplete = useCallback(
    (operationId: string) => {
      dispatch({
        type: CreateSourceFileUploadActionType.UPDATE_FILE_PROGRESS,
        payload: {
          fileId: operationId,
          updates: { status: SourceSyncOperationStatus.COMPLETED, percentage: 100 },
        },
      });
    },
    [dispatch],
  );

  const onUploadComplete = useCallback(() => {
    dispatch({
      type: CreateSourceFileUploadActionType.SET_PHASE,
      payload: CreateSourceFileUploadPhase.COMPLETED,
    });
    dispatch({ type: CreateSourceFileUploadActionType.SET_OVERALL_PROGRESS, payload: 100 });
  }, [dispatch]);

  const { uploadSyncFiles } = useFileUploader({
    files: state.files,
    onSyncStart,
    onInitFileProgress,
    onFileStart,
    onPartComplete,
    onFileComplete,
    onUploadComplete,
  });

  const canSubmit = useMemo(() => canSubmitUpload(state), [state]);
  const isDisabled = useMemo(() => isUploadDisabled(state), [state]);
  const nameError = useMemo(() => getCreateSourceNameError(state), [state]);
  const fileProgressMap = useMemo(
    () => getFileProgressMap(state.fileProgressMap),
    [state.fileProgressMap],
  );

  const startUpload = useCallback(async () => {
    dispatch({ type: CreateSourceFileUploadActionType.SET_SHOULD_SHOW_ERRORS, payload: true });

    if (
      state.isUploading ||
      !isCreateSourceValueProvided(state.sourceName) ||
      state.files.length === 0
    ) {
      return;
    }

    let failedOperation = CreateSourceFileUploadOperation.CREATE_SOURCE;
    try {
      dispatch({
        type: CreateSourceFileUploadActionType.SET_PHASE,
        payload:
          CREATE_SOURCE_FILE_UPLOAD_OPERATION_STEP_TO_PHASE_MAP[
            CreateSourceFileUploadOperation.CREATE_SOURCE
          ],
      });

      const createRequest = buildCreateSourceFileUploadRequest(state.sourceName, state.files);
      const createSourceResponse = await createSourceMutation(createRequest);
      const provisioningResult = extractProvisioningResult(createSourceResponse);

      failedOperation = CreateSourceFileUploadOperation.UPLOAD_FILES;
      dispatch({
        type: CreateSourceFileUploadActionType.SET_PHASE,
        payload:
          CREATE_SOURCE_FILE_UPLOAD_OPERATION_STEP_TO_PHASE_MAP[
            CreateSourceFileUploadOperation.UPLOAD_FILES
          ],
      });

      await uploadSyncFiles(provisioningResult.uploadSync);

      void queryClient.invalidateQueries({ queryKey: createListSourcesInfiniteQueryKey() });

      showToast({
        variant: ToastVariant.SUCCESS,
        header: "Source created",
        subheader: "The source has been created and files uploaded successfully",
      });
      if (onSuccess) onSuccess(provisioningResult.sourceId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : CREATE_SOURCE_UNEXPECTED_ERROR;
      dispatch({
        type: CreateSourceFileUploadActionType.SET_ERROR,
        payload: errorMessage,
      });
      const isDenied = isPermissionError(error);
      showToast({
        variant: ToastVariant.ERROR,
        header: isDenied
          ? CREATE_SOURCE_PERMISSION_DENIED_HEADER
          : CREATE_SOURCE_FILE_UPLOAD_OPERATION_STEP_TO_ERROR_TOAST_HEADER_MAP[failedOperation],
        subheader: isDenied ? CREATE_SOURCE_PERMISSION_DENIED_SUBHEADER : errorMessage,
      });
    }
  }, [
    state.isUploading,
    state.sourceName,
    state.files,
    dispatch,
    createSourceMutation,
    uploadSyncFiles,
    queryClient,
    showToast,
    onSuccess,
  ]);

  return {
    state,
    dispatch,
    startUpload,
    canSubmit,
    isDisabled,
    nameError,
    fileProgressMap,
  };
}
